import React, { type MouseEvent, useCallback } from "react";
import { Form } from "react-final-form";
import { FormattedMessage } from "react-intl";
import { Button, Col, Row } from "reactstrap";
import { SubmissionError } from "redux-form";
import { ResendOTPMessage } from "src/authentication/components/ResendOTPMessage";
import { useAuthenticateOtp } from "src/authentication/hooks/useAuthenticateOtp";
import type { AuthenticationOTPContext } from "src/authentication/objects/Authentication";
import { ReactQueryResult } from "src/shared/components/ReactQueryResult";
import { useFormLoadErrors } from "src/shared/forms/hooks/useFormLoadErrors";
import TextInputNew from "src/shared/forms/inputs/TextInputNew";

export const OTP = "otp";

export interface IOTPFormData extends FormData {
  [OTP]: string;
}

export interface IOTPFormProps {
  authenticationOtpContext: AuthenticationOTPContext;
  onCancelOtp: () => void;
  onContextReset: (authenticationOtpContext: AuthenticationOTPContext) => void;
}

export const OTPForm = ({
  authenticationOtpContext,
  onContextReset,
  onCancelOtp,
}: IOTPFormProps) => {
  const authenticationMutation = useAuthenticateOtp();
  const handleCancelOtp = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      onCancelOtp();
    },
    [onCancelOtp],
  );

  const handleFormSubmit = useCallback(
    async ({ [OTP]: otp }: IOTPFormData) => {
      try {
        await authenticationMutation.mutateAsync({
          ...authenticationOtpContext,
          otp,
        });
      } catch (ex) {
        throw new SubmissionError({
          [OTP]: (
            <ResendOTPMessage
              authenticationOtpContext={authenticationOtpContext}
              onContextReset={onContextReset}
            />
          ),
        });
      }
    },
    [authenticationMutation, authenticationOtpContext, onContextReset],
  );

  return (
    <Form onSubmit={useFormLoadErrors(handleFormSubmit)}>
      {({
        handleSubmit,
        pristine,
        submitting,
        hasValidationErrors,
        hasSubmitErrors,
        modifiedSinceLastSubmit,
      }) => (
        <form onSubmit={handleSubmit}>
          <p>
            <FormattedMessage id="authentication.otp.description" />
          </p>
          <Row>
            <Col sm={12}>
              <TextInputNew
                inputID={OTP}
                label={<FormattedMessage id="authentication.otp" />}
                isRequired
              />
            </Col>
          </Row>
          {authenticationMutation.isLoading && (
            <ReactQueryResult {...authenticationMutation} />
          )}
          <p>
            <Button
              type="submit"
              color="primary"
              disabled={
                pristine ||
                submitting ||
                hasValidationErrors ||
                (hasSubmitErrors && !modifiedSinceLastSubmit)
              }
            >
              <FormattedMessage id="authentication.otp.submit" />
            </Button>
            <a href="" onClick={handleCancelOtp}>
              <FormattedMessage id="authentication.otp.returnToLogin" />
            </a>
          </p>
        </form>
      )}
    </Form>
  );
};
